import { Autocomplete, Button, TextField } from "@mui/material";
import React from "react";
import { fileToBase64 } from "utils/fileToBase64";

/**
 * Select con buscador para la barra de filtros.
 *
 * Es el gemelo de `FormikAutocompleteInput` de components/UI/Inputs, con una sola
 * diferencia que importa: aquel se alimenta del CONTEXTO de Formik (`useField`), y acá
 * no hay contexto. `Filters` recibe una instancia de `useFormik()` por prop, que no
 * monta ningún provider, así que todos los inputs de esta carpeta leen y escriben a
 * través de ese objeto. Por eso existe esta carpeta duplicada y no se puede reusar el
 * de los modales.
 *
 * Igual que su gemelo, FORMIK GUARDA EL VALOR, no la opción: el filtro sigue mandando
 * un cod al servicio y no se entera de que el campo cambió de desplegable a buscador.
 *
 * La opción "Todos" no es una opción de la lista: es el valor vacío, al que se vuelve
 * con la X de limpiar.
 */
export function FormikAutocompleteInput({
    data,
    name,
    formik,
    disabled,
    options = [],
    optionValue = "id",
    optionLabel = "label",
    className,
    anchoInput,
}) {
    // `String(...)` a los dos lados: los catálogos mezclan ids numéricos con valores de
    // formik que viajan como texto, y sin normalizar el precargado no matchea ninguna
    // opción y el campo abre vacío.
    const seleccionada =
        options.find((o) => String(o[optionValue]) === String(formik.values[name])) ?? null;

    return (
        <Autocomplete
            className={className}
            disabled={disabled}
            fullWidth={className ? false : true}
            options={options}
            value={seleccionada}
            onChange={(_evento, opcion) =>
                formik.setFieldValue(name, opcion ? opcion[optionValue] : "")
            }
            getOptionLabel={(opcion) => String(opcion?.[optionLabel] ?? "")}
            isOptionEqualToValue={(opcion, valor) =>
                String(opcion[optionValue]) === String(valor?.[optionValue])
            }
            noOptionsText="Sin resultados"
            // MUI rotula en inglés los iconos del campo; son tooltips y texto de lector
            // de pantalla, así que van traducidos acá y no en cada uso.
            clearText="Limpiar"
            openText="Abrir"
            closeText="Cerrar"
            // El ancho por defecto de la barra de filtros son 138px, que alcanzan para
            // una fecha o un código pero no para el nombre de un hospital.
            sx={anchoInput ? { "& .MuiInputBase-root": { width: `${anchoInput} !important` } } : undefined}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={data[name].label}
                    margin="normal"
                    placeholder={data[name].placeholder ?? "Todos"}
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                />
            )}
        />
    );
}

export function FormikTextInput({ data, name, formik, disabled, className }) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            placeholder={data[name].placeholder}
            margin="normal"
            type="text"
            disabled={disabled}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        />
    );
}

export function FormikNumberInput({ data, name, formik, disabled, className }) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            placeholder={data[name].placeholder}
            margin="normal"
            type="number"
            disabled={disabled}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        />
    );
}

export function FormikSelectInput({
    data,
    name,
    formik,
    disabled,
    options,
    optionKey,
    optionValue,
    optionName,
    className,
    all,
    selectDefault,
    anchoInput,
}) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            margin="normal"
            select
            disabled={disabled}
            SelectProps={{ native: true }}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
            sx={anchoInput ? { "& .MuiInputBase-root": { width: `${anchoInput} !important` } } : undefined}
        >
            {all && <option value="">Todos</option>}
            {options.map((option, index) => {
                return (
                    <option key={option[optionKey] || index} value={option[optionValue]} selected={index === selectDefault ? true : false}>
                        {option[optionName]}
                    </option>
                );
            })}
        </TextField>
    );
}

export function FormikYesOrNoInput({
    data,
    name,
    formik,
    disabled,
    className,
}) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            margin="normal"
            select
            disabled={disabled}
            SelectProps={{ native: true }}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        >
            <option value="Y">Si</option>
            <option value="N">No</option>
        </TextField>
    );
}

export function FormikDateInput({ data, name, formik, disabled, className }) {

    return (
        <TextField
            label={data[name].label}
            name={name}
            placeholder={data[name].placeholder}
            margin="normal"
            type="date"
            disabled={disabled}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        />
    );
}

export function FormikTimeInput({ data, name, formik, disabled, className }) {
    return (
        <TextField
            label={data[name].label}
            name={name}
            placeholder={data[name].placeholder}
            margin="normal"
            type="time"
            disabled={disabled}
            value={formik.values[name]}
            onChange={formik.handleChange}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            helperText={formik.touched[name] && formik.errors[name]}
            InputLabelProps={{ shrink: true }}
            fullWidth={className ? false : true}
            className={className}
        />
    );
}

export function FormikFileInput({ data, name, formik, disabled, className }) {
    const handleFile = async (e) => {
        const file = e.target.files[0];
        const fileData = await fileToBase64(file);
        formik.setFieldValue(name + "File", fileData);
    };

    return (
        <Button
            variant="contained"
            component="label"
            margin="normal"
            disabled={disabled}
            error={formik.touched[name] && Boolean(formik.errors[name])}
            fullWidth={className ? false : true}
            className={className}
        >
            {data[name].label}
            <input hidden type="file" name={name} onChange={handleFile} />
        </Button>
    );
}
